import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TextField, Typography,
} from '@mui/material';
import PageLayout from '../shell/PageLayout';
import { request, useOperations } from '../operations/OperationsProvider';
import { driverContact, driverLabel } from '../operations/drivers';
import { inWords } from '../operations/stageWords';
import DownloadTable from '../shell/DownloadTable';
import OperationsStatus from '../operations/OperationsStatus';
import useOperationalPermissions from '../common/util/operationalPermissions';
import ScopeFilters, { useScope } from '../operations/ScopeFilters';
import { located } from '../operations/OperationsMap';
import { formatDateTime, plantMidnight } from '../common/util/datetime';
import useTripTrail from '../operations/useTripTrail';
import { awaitingReturn } from '../operations/devices';
import MilestonePanel from './MilestonePanel';
import ChallanPanel from './ChallanPanel';
import DelayPanel from './DelayPanel';
import { challanOwed } from './challan';
import TripPlayback from './TripPlayback';
import TripArrival from '../operations/TripArrival';
import IssueTable from '../attention/IssueTable';
import { useZeny } from '../attention/ZenyProvider';
import GateDesk from './GateDesk';
import GateOutDesk from './GateOutDesk';
import { accentChipSx, stageAccent } from '../common/theme/accents';

// What a closure reason is called at the counter. Anything not named here is shown as its own name,
// tidied up, which is how every other code in this product is read back.
const CLOSURES = {
  UNLOAD_COMPLETE: 'Unloaded and gone',
  DESTINATION_REACHED: 'Delivered at the customer',
  DOCUMENTS: 'Closed against documents',
  CANCELLED: 'Trip cancelled',
  WRONG_TRIP: 'Wrong trip entered',
  VEHICLE_EXITED_PLANT: 'Left without a gate out',
  DEVICE_RETURNED: 'Tracking device returned',
  OTHER: 'Something else',
  MANUAL: 'Completed manually by an administrator',
};
const label = (value) => CLOSURES[value] || inWords(value);

// The trip list as a spreadsheet: the columns that are on screen, in that order.
//
// The driver is split into two columns here where the table shows one line, because a column of
// numbers is what a spreadsheet is for - sorting, pasting into a dialler, matching against a
// transporter's own list - and a joined string is none of those.
const tripColumns = (drivers) => [
  ['Trip', (t) => `Q-${t.id}`],
  ['Vehicle', (t) => t.vehicle || ''],
  ['Shipment ID', (t) => t.shipmentId || ''],
  ['Destination', (t) => t.destination || ''],
  ['Movement', (t) => label(t.movementType)],
  ['Stage', (t) => label(t.closedAt ? 'CLOSED' : t.stage)],
  ['Driver', (t) => driverContact(drivers, t).name],
  ['Driver mobile', (t) => driverContact(drivers, t).mobile],
];
// The trip record's own field names are not what the yard calls these things.
const fieldNames = {
  shipmentId: 'Shipment ID',
  challanNumber: 'Challan / store entry / order number',
  consignmentId: 'Consignment',
  consignor: 'Origin / consignor',
  driver: 'Driver',
};
const time = (value) => (value ? formatDateTime(value) : '—');
// When the load actually arrived: at the customer for a despatch, at this plant for a delivery.
// The server records it on the trip as it happens; a trip closed before it did is read the same way
// here, so an old record still answers the question.
const kg = (value) => `${Number(value).toLocaleString('en-IN')} kg`;
const detectionText = (d) => [
  time(d.detectedAt),
  d.weighingRole && `${label(d.weighingRole)}${d.weightKg != null ? ` ${kg(d.weightKg)}` : ''}`,
  d.ticketNumber && `Ticket ${d.ticketNumber}`,
  d.segmentMs != null && `${(d.segmentMs / 60000).toFixed(1)} min segment`,
  d.method === 'MANUAL' ? `Recorded by ${d.recordedByName}` : d.method && `${d.method} detection`,
].filter(Boolean).join(' · ');
const initial = {
  vehicleId: '', routeId: '', driverId: '', movementType: 'LOAD', direction: 'OUTBOUND', shipmentId: '', consignmentId: '', transporter: '', consignor: '', material: '', quantity: '', quantityUnit: 't',
};

const TripsPage = ({ view }) => {
  const views = {
    gate: ['Gate Operations', 'Open a trip to record gate entry, exit or checkpoint activity.'],
    plant: ['Plant Tracking', 'Active trips inside the plant. Open one to review checkpoint progress.'],
    outbound: ['Outbound Tracking', 'Outbound trips in transit. Open one for milestones, or open the map.'],
  };
  const basePath = { gate: '/gate-operations', plant: '/plant-tracking', outbound: '/outbound-tracking' }[view] || '/trips';
  const { data, trips, loading, error: loadError, updatedAt, mutate, analytics } = useOperations();
  // Which way the traffic is going at this desk. Unset means "whatever the gate I am posted at is
  // for", which is right nearly always and wrong only at a gate that takes both.
  const [direction, setDirection] = useState('');
  const postedAt = useSelector((s) => s.session.user?.postedCheckpointId);
  const postedGate = (data.checkpoints || []).find((c) => c.id === postedAt);
  const atExit = direction ? direction === 'OUT' : postedGate?.type === 'GATE_OUT';
  // What was raised against this load while it ran. A trip is read when somebody is answering for
  // it, and the first question is always what went wrong - which until now meant leaving the trip,
  // opening the exception list and searching it by number.
  const { items: raised } = useZeny();
  const positions = useSelector((s) => s.positions.items);
  const { operate, administer } = useOperationalPermissions();
  const navigate = useNavigate();
  const { id } = useParams();
  const [filters, setFilters] = useSearchParams();
  const scope = useScope();
  const [page, setPage] = useState(0); const [pageSize, setPageSize] = useState(25);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(initial);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [acceptNote, setAcceptNote] = useState('');
  const [challan, setChallan] = useState('');
  // At the gate the register is reference material, not the work. It stays one tap away rather
  // than occupying the screen above the truck that is actually standing there.
  const [register, setRegister] = useState(false);
  const [zoom, setZoom] = useState(false);
  // Where the list itself starts. A tile on the dashboard is a count and a promise to show the
  // trips behind it; landing above a page of filters, with the table below the fold, breaks the
  // promise quietly - the reader sees the screen they just left and assumes nothing happened.
  const list = useRef(null);
  useEffect(() => setShare(null), [id]);
  useEffect(() => {
    if (id || !['today', 'status', 'standing', 'inside', 'phase'].some((key) => filters.get(key))) return undefined;
    // After the rows arrive, so the page is its final height when it is scrolled rather than a
    // skeleton that grows underneath the reader.
    const settle = window.setTimeout(() => list.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    return () => window.clearTimeout(settle);
  }, [id, filters.get('today'), filters.get('status'), filters.get('standing'), filters.get('inside'), updatedAt]);
  const trip = trips.find((t) => String(t.id) === id);
  const tripIssues = trip ? raised.filter((issue) => issue.tripId === trip.id).sort((a, b) => Date.parse(b.occurredAt || b.raisedAt) - Date.parse(a.occurredAt || a.raisedAt)) : [];
  // The number to ring about this load, taken from the driver the trip was opened against.
  const driverMobile = trip ? (data.drivers || []).find((who) => who.id === trip.driverId)?.mobile || '' : '';
  // The photograph the list deliberately leaves out, fetched for the one trip being looked at. The
  // list says whether there is one, so a trip without a photograph costs no request at all.
  const hasChallanImage = !!trip?.hasChallanImage;
  useEffect(() => {
    let cancelled = false;
    setChallan(''); setZoom(false);
    if (!id || !hasChallanImage) return undefined;
    request(`/api/operations/trips/${id}`)
      .then((full) => { if (!cancelled) setChallan(full.challanImageDataUrl || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, hasChallanImage]);
  const history = useTripTrail(trip?.id, updatedAt);
  const samples = history.items;
  const field = (key) => ({ value: form[key] || '', onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })), fullWidth: true });
  // react-router 6.3 has no functional form of setSearchParams (ScopeFilters notes the same), so
  // the next parameters are built from the current ones.
  const changeFilter = (key, value) => { const next = new URLSearchParams(filters); if (value) next.set(key, value); else next.delete(key); setFilters(next); };
  const open = (value) => { setError(null); setForm({}); setDialog(value); };
  const submit = async (event) => {
    event.preventDefault(); setPending(true); setError(null);
    try {
      if (dialog === 'close') await mutate(`trips/${trip.id}/close`, form);
      setDialog(null);
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const shareTrip = async () => {
    setPending(true); setError(null);
    try { setShare(await mutate(`trips/${trip.id}/share`, {})); } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const revokeShare = async (shareId) => {
    setPending(true); setError(null);
    try { await mutate(`shares/${shareId}`, null, 'DELETE'); setShare(null); } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const returnDevice = async (assignmentId) => {
    setPending(true); setError(null);
    try { await mutate(`assignments/${assignmentId}/return`, {}); } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  const acceptReconciliation = async () => {
    setPending(true); setError(null);
    try { await mutate(`trips/${trip.id}/reconciliation`, { note: acceptNote }); setAcceptNote(''); } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  // Devices released from this vehicle that have not been handed back yet. The closed trip is
  // where staff are when the truck leaves, so the return is recorded here rather than in setup.
  const returns = trip ? awaitingReturn(data.assignments, data.sources).filter((a) => a.vehicleId === trip.vehicleId) : [];
  const deviceName = (sourceId) => { const source = data.sources.find((s) => s.id === sourceId); return source ? `${source.kind} device ${source.identifier}` : 'Tracking device'; };
  // Opened, closed, despatched or arrived today: the four things the dashboard counts for the day.
  // Each is matched here by the same event the tile counts, so the list is exactly the trips behind
  // the number - and because it is a day of its own, it ignores the date range in the filters above,
  // which cuts on when a trip was created and would drop a trip closed today but opened yesterday.
  const today = filters.get('today');
  const since = plantMidnight();
  // Where a truck is standing now, by the last stage anybody recorded for it - the same reading the
  // dashboard uses to count the trucks at a bay, so the tile and this list cannot disagree.
  const standingAt = (trip) => {
    const last = [...(trip.detections || [])].reverse().find((detection) => !detection.deviation);
    return last ? (trip.checkpoints || []).find((point) => point.id === last.checkpointId) : null;
  };
  const standing = filters.get('standing');
  // What a truck is doing now: inside the plant, or on the road. The dashboard counts these, so the
  // list it opens has to be able to hold the same question.
  const phase = filters.get('phase');
  // Inside longer than the plant allows: gated in, not gated out, and past the configured limit.
  const insideLimit = analytics?.longStayMs || 0;
  const inside = filters.get('inside') === 'long';
  const happenedToday = (trip) => {
    if (!today) return true;
    const at = { opened: trip.createdAt, closed: trip.closedAt, despatched: trip.gateOut, arrived: trip.closedAt }[today];
    if (!at || Date.parse(at) < since) return false;
    return today !== 'arrived' || trip.closureReason === 'DESTINATION_REACHED';
  };
  const todayLabel = {
    opened: 'opened today', closed: 'closed today', despatched: 'despatched today', arrived: 'arrived at the customer today',
  }[today] || (standing === 'BAY' ? 'standing at a bay' : inside ? `inside the plant longer than ${Math.round((insideLimit / 3600000) * 10) / 10} hours` : phase === 'IN_PLANT' ? 'inside the plant now' : phase === 'IN_TRANSIT' ? 'on the road now' : '');
  const filtered = trips.filter((t) => happenedToday(t)
    && (!phase || (!t.closedAt && t.phase === phase))
    && (!standing || (!t.closedAt && standingAt(t)?.type === standing))
    && (!inside || (!!t.gateIn && !t.gateOut && !t.closedAt && insideLimit > 0 && Date.now() - Date.parse(t.gateIn) > insideLimit))
    && (view !== 'plant' || (!t.closedAt && t.phase === 'IN_PLANT'))
    && (view !== 'outbound' || (!t.closedAt && t.phase === 'IN_TRANSIT' && t.direction === 'OUTBOUND'))
    && scope.matches(t, !today && !standing && !inside && !phase && filters.get('status') !== 'ACTIVE' && view !== 'plant' && view !== 'outbound') && (!filters.get('status') || (filters.get('status') === 'ACTIVE' ? !t.closedAt : !!t.closedAt))
    && (!filters.get('direction') || t.direction === filters.get('direction'))
    && (!filters.get('movement') || t.movementType === filters.get('movement'))
    && `${t.id} ${t.vehicle} ${t.shipmentId} ${t.consignmentId}`.toLowerCase().includes((filters.get('q') || '').toLowerCase()))
    .sort((a, b) => Number(!!a.closedAt) - Number(!!b.closedAt) || Date.parse(a.detections?.at(-1)?.detectedAt || a.createdAt) - Date.parse(b.detections?.at(-1)?.detectedAt || b.createdAt));
  // A portable unit issued for this trip and not yet handed back.
  const outstandingDevice = trip && (data.assignments || []).some((a) => a.vehicleId === trip.vehicleId && a.mode === 'TEMPORARY' && !a.returnedAt);
  const reasons = trip ? [
    ...(!trip.gateIn ? ['CANCELLED'] : []),
    ...(trip.movementType === 'UNLOAD' && trip.gateOut ? ['UNLOAD_COMPLETE'] : []),
    ...(trip.movementType === 'LOAD' && trip.gateOut && trip.stage === 'CLIENT' ? ['DESTINATION_REACHED'] : []), 'DOCUMENTS',
    // What a desk actually sees: the wrong lorry, a lorry that left unrecorded, or something else
    // nobody listed. Each carries the operator's own account of it.
    'WRONG_TRIP', 'VEHICLE_EXITED_PLANT',
    // The hired truck has gone and the portable unit is back over the counter, which is the moment
    // that trip is actually finished with. Offered only where there is a unit out to hand back.
    ...(outstandingDevice ? ['DEVICE_RETURNED'] : []), 'OTHER',
    // The override has no stage precondition by design, so it is offered only to
    // administrators and always carries a written reason.
    ...(administer ? ['MANUAL'] : []),
  ] : [];

  return (
    <PageLayout title={trip ? `Trip Q-${trip.id}` : views[view]?.[0] || 'Trips'} subtitle={trip ? `${trip.vehicle} · ${label(trip.movementType)} · ${trip.shipmentId}` : views[view]?.[1] || 'One trip, from the first checkpoint to its final destination.'}>
      {(view === 'plant' || view === 'outbound') && <Button sx={{ mb: 2 }} variant="outlined" onClick={() => navigate(basePath)}>Open map</Button>}
      <OperationsStatus />
      {history.error && <Alert severity="warning">{history.error}</Alert>}
      {/* The desk belongs to the gate, not to the register. `!view` put a whole admission form at
          the top of /trips, so looking a load up meant scrolling past the form for admitting a new
          one - and the menu entry called Trips opened on "Admit a truck at the gate". */}
      {/* A gate is a way in or a way out, and the man standing at it is doing one of those two
          things. Which one is answered by the gate he says he is posted at: a GATE_OUT means the
          barrier he is working is the one on the way out, so he gets the exit desk. A gate that
          takes both directions, and an operator who has not said where he is, get the switch - this
          is not something to guess at a barrier. */}
      {!id && operate && view === 'gate' && (
        <>
          <Stack direction="row" gap={1} sx={{ mb: 2 }}>
            <Button variant={atExit ? 'outlined' : 'contained'} onClick={() => setDirection('IN')}>Plant entry</Button>
            <Button variant={atExit ? 'contained' : 'outlined'} onClick={() => setDirection('OUT')}>Plant exit</Button>
          </Stack>
          {atExit
            ? <GateOutDesk trips={trips} />
            : <GateDesk trips={trips} basePath={basePath} />}
        </>
      )}
      {!id && view === 'gate' && (
        <Button
          sx={{ mb: 2 }}
          onClick={() => setRegister((open) => !open)}
        >
          {register ? 'Hide recent trips' : `Recent trips at this gate (${trips.filter((t) => !t.closedAt).length} open)`}
        </Button>
      )}
      {!id && (view !== 'gate' || register) && <ScopeFilters scope={scope} />}
      {!dialog && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {id && !trip && !loading && updatedAt && (
      <Alert severity="info">
        Trip not found in your accessible records.
        <Button onClick={() => navigate(basePath)}>Back to trips</Button>
      </Alert>
      )}
      {trip ? (
        <>
          <TripPlayback
            height={340}
            label={trip.vehicle}
            live={!trip.closedAt}
            latest={!trip.closedAt && located(positions[trip.vehicleId]) ? positions[trip.vehicleId] : null}
            sites={trip.checkpoints.filter(located).map((c) => ({ ...c, id: `checkpoint-${c.id}`, label: c.name, color: '#64748b', kind: 'site' }))}
            plan={trip.checkpoints.filter(located)}
            trail={samples.filter((s) => s.tripId === trip.id && s.sourceKind === 'GPS').sort((a, b) => Date.parse(a.fixTime) - Date.parse(b.fixTime))}
            detections={(trip.detections || []).filter((d) => !d.deviation)}
            limitKph={Number(data.config?.[trip.phase === 'IN_PLANT' ? 'plantSpeedKph' : 'roadSpeedKph']) || 0}
          />
          {/* A thinned trail still looks continuous, and short halts between two kept fixes leave no
              trace in it - which is the one thing somebody replaying a trip is usually counting. */}
          {history.thinned && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {`Showing ${history.items.length.toLocaleString('en-IN')} of ${history.total.toLocaleString('en-IN')} recorded fixes. The trail is thinned evenly across the trip, so brief halts between the fixes shown may not appear.`}
            </Alert>
          )}
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
            <Button onClick={() => navigate(basePath)}>Back to trips</Button>
            <Chip label={label(trip.phase)} />
            <Chip label={label(trip.stage)} sx={accentChipSx(stageAccent(trip.stage))} />
            {operate && !trip.closedAt && (
            <>
              <Button disabled={pending || !!loadError} onClick={() => open('close')}>Close trip</Button>
              <Button disabled={pending || !!loadError} onClick={shareTrip}>Share tracking</Button>
            </>
            )}
          </Stack>
          {share && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <a href={`/track/${share.token}`} target="_blank" rel="noreferrer">Open tracking link</a>
            {` · Expires ${time(share.expiresAt)}`}
          </Alert>
          )}
          {!trip.closedAt && (data.shares || []).filter((s) => s.tripId === trip.id && !s.revokedAt && Date.parse(s.expiresAt) > Date.now()).map((s) => <Alert key={s.id} severity="info" sx={{ mb: 2 }} action={<Button disabled={pending} onClick={() => revokeShare(s.id)}>Revoke</Button>}>{`Your tracking link · Expires ${time(s.expiresAt)}`}</Alert>)}
          {operate && !trip.closedAt && challanOwed(trip).length > 0 && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Challan details</Typography>
            <ChallanPanel trip={trip} />
          </Paper>
          )}
          {operate && !trip.closedAt && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Record stage</Typography>
            <MilestonePanel trip={trip} />
          </Paper>
          )}
          {/* Where this load's time went. Shown for a finished load as much as a running one - after
              the fact is when somebody asks why it was late - and not shown at all where no leg has
              been measured, because a heading with nothing under it reads as a fault. */}
          {!!trip.delay?.legs?.length && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Where the time went</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each stop and each stretch of road against what it normally takes on this route.
            </Typography>
            <DelayPanel trip={trip} />
          </Paper>
          )}
          {operate && trip.closedAt && returns.map((a) => (
            <Alert key={a.id} severity="warning" sx={{ mb: 2 }} action={<Button disabled={pending} onClick={() => returnDevice(a.id)}>Record return</Button>}>
              {`${deviceName(a.sourceId)} was released from this vehicle at ${time(a.endedAt)} and is not back yet.`}
            </Alert>
          ))}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Trip milestones</Typography>
              {trip.checkpoints.map((checkpoint, index) => {
                const detection = trip.detections.find((d) => d.checkpointId === checkpoint.id && !d.deviation);
                return (
                  <Stack key={checkpoint.id} direction="row" gap={2} sx={{ pb: 3 }}>
                    <Chip label={index + 1} color={detection ? 'primary' : 'default'} />
                    <Box>
                      <Typography fontWeight={600}>{checkpoint.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{detection ? detectionText(detection) : index === trip.progress && !trip.closedAt ? 'Next expected milestone' : 'Not recorded'}</Typography>
                      {detection?.correctionReason && <Typography variant="caption" color="warning.main">{`Time corrected: ${detection.correctionReason}`}</Typography>}
                    </Box>
                  </Stack>
                );
              })}
              {trip.detections.filter((d) => d.deviation).map((d) => <Alert key={`${d.detectedAt}-${d.checkpointId}`} severity="warning" sx={{ mb: 1 }}>{`${d.name} detected out of sequence at ${time(d.detectedAt)}`}</Alert>)}
            </Paper>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Trip details</Typography>
              {['shipmentId', 'challanNumber', 'consignmentId', 'transporter', 'consignor', 'consignee', 'destination', 'material', 'driver', 'direction'].filter((key) => String(trip[key] ?? '').trim()).map((key) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">{fieldNames[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</Typography>
                  <Typography>{trip[key]}</Typography>
                </Box>
              ))}
              {/* The driver's number is on the trip because somebody reading it at two in the
                  morning wants to ring the lorry, not look the driver up in another register. */}
              {driverMobile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Driver number</Typography>
                <Typography>{driverMobile}</Typography>
              </Box>
              )}
              {/* The hours the load took, in the terms the plant argues about them in: when it
                  entered, when it left, and how long it stood inside. */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Gate in · Gate out</Typography>
                <Typography>{`${time(trip.gateIn)} · ${time(trip.gateOut)}`}</Typography>
                {trip.gateIn && trip.gateOut && <Typography variant="body2" color="text.secondary">{`${Math.round((Date.parse(trip.gateOut) - Date.parse(trip.gateIn)) / 60000)} min inside the plant`}</Typography>}
              </Box>
              {/* The two times a customer asks about: when the load was expected, and when it
                  turned up. The estimate is kept current by the server while the trip runs, so this
                  reads it rather than working it out. */}
              <TripArrival trip={trip} maxAgeMs={data.config.reportingIntervalMs || 300000} />
              <Typography>{`${trip.quantity} ${trip.quantityUnit}`}</Typography>
              {/* The challan as it was presented at the counter. Kept because what the clerk typed
                  and what the paper said are two different records, and a dispute is about the paper. */}
              {challan && (
              <Box sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">Challan as presented</Typography>
                <Box
                  component="img"
                  src={challan}
                  alt={`Challan ${trip.shipmentId || ''} as presented at intake`}
                  onClick={() => setZoom(true)}
                  sx={{
                    display: 'block', mt: 0.5, width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', cursor: 'zoom-in',
                  }}
                />
              </Box>
              )}
              {trip.netWeightKg != null && (
              <Box sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">Net weight</Typography>
                <Typography>{kg(trip.netWeightKg)}</Typography>
                {trip.weightVarianceKg != null && <Typography variant="body2" color="text.secondary">{`${trip.weightVarianceKg >= 0 ? '+' : '−'}${kg(Math.abs(trip.weightVarianceKg))} (${trip.weightVariancePercent >= 0 ? '+' : '−'}${Math.abs(trip.weightVariancePercent)}%) against the challan`}</Typography>}
              </Box>
              )}
              {trip.reconciliationStatus && (
              <Box sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">ERP reconciliation</Typography>
                <Typography>{`${label(trip.reconciliationStatus)}${trip.reconciledOrderId ? ` · order ${trip.reconciledOrderId}` : ''}`}</Typography>
                {trip.recordSource === 'MANUAL' && trip.manualReason && <Typography variant="body2" color="text.secondary">{`Manual intake: ${trip.manualReason}`}</Typography>}
                {(trip.reconciliationDifferences || []).map((d) => <Typography key={d.field} variant="body2" color="warning.main">{`${d.label}: trip ${d.trip}, order ${d.order}`}</Typography>)}
                {trip.reconciliationNote && <Typography variant="body2" color="text.secondary">{`Accepted by ${trip.reconciledByName}: ${trip.reconciliationNote}`}</Typography>}
                {administer && ['PENDING', 'MISMATCH'].includes(trip.reconciliationStatus) && (
                <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                  <TextField size="small" label="Reason to accept as recorded" value={acceptNote} onChange={(e) => setAcceptNote(e.target.value)} inputProps={{ maxLength: 500 }} sx={{ flex: 1 }} />
                  <Button disabled={pending || !acceptNote.trim()} onClick={acceptReconciliation}>Accept</Button>
                </Stack>
                )}
              </Box>
              )}
              <Typography variant="body2">{`Created ${time(trip.createdAt)}`}</Typography>
              {trip.closedAt && <Alert sx={{ mt: 2 }} severity={trip.closureReason === 'MANUAL' ? 'warning' : 'info'}>{`${label(trip.closureReason)} · ${time(trip.closedAt)}${trip.documentReference ? ` · ${trip.documentReference}` : ''}${trip.closedByName ? ` · by ${trip.closedByName}` : ''}${trip.closureComment ? ` — ${trip.closureComment}` : ''}`}</Alert>}
            </Paper>
          </Box>
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Exceptions and alerts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Speed, route deviation, stoppages and everything else raised against this trip.</Typography>
            <IssueTable items={tripIssues} defaultStatus="" />
          </Paper>
        </>
      ) : !id && (view !== 'gate' || register) && (
        <Box ref={list} sx={{ scrollMarginTop: 16 }}>
          {/* Why this list is narrower than the filters above suggest, and how to widen it again:
              a reader who arrived from a tile should never have to guess which trips are missing. */}
          {todayLabel && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={(
                <Button
                  size="small"
                  onClick={() => { const next = new URLSearchParams(filters); ['today', 'standing', 'inside', 'phase'].forEach((key) => next.delete(key)); setFilters(next); }}
                >
                  Show all trips
                </Button>
              )}
            >
              {`Showing the ${filtered.length} ${filtered.length === 1 ? 'trip' : 'trips'} ${todayLabel}.`}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 3 }}>
            <TextField label="Search trips" value={filters.get('q') || ''} onChange={(e) => changeFilter('q', e.target.value)} sx={{ flex: 1 }} />
            <TextField select label="Status" value={filters.get('status') || ''} onChange={(e) => changeFilter('status', e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </TextField>
          </Stack>
          {/* Every row the filters have left, not the page being shown: the pages are a window on
              one set of results, and somebody who filtered to a fortnight wants the fortnight. */}
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <DownloadTable name="trips" rows={filtered} columns={tripColumns(data.drivers)} />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead><TableRow>{['Trip', 'Vehicle', 'Shipment ID', 'Destination', 'Movement', 'Stage', 'Driver'].map((c) => <TableCell key={c}>{c}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {filtered.slice(Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1)) * pageSize, (Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1)) + 1) * pageSize).map((t) => (
                  <TableRow key={t.id} hover tabIndex={0} aria-label={`Open trip Q-${t.id}`} sx={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/${t.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`${basePath}/${t.id}`); }}>
                    <TableCell>{`Q-${t.id}`}</TableCell>
                    <TableCell>{t.vehicle}</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}>{t.shipmentId}</TableCell>
                    <TableCell>{t.destination || '—'}</TableCell>
                    <TableCell>{label(t.movementType)}</TableCell>
                    <TableCell><Chip size="small" label={label(t.closedAt ? 'CLOSED' : t.stage)} sx={accentChipSx(stageAccent(t.closedAt ? 'CLOSED' : t.stage))} /></TableCell>
                    <TableCell>{driverLabel(data.drivers, t) || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filtered.length} page={Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1))} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100]} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} />
          {updatedAt && !loadError && filtered.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6">{trips.length ? 'No matching trips' : 'Ready for your first trip'}</Typography>
            <Typography color="text.secondary">{trips.length ? 'Adjust your filters to see more trips.' : 'Set up your gates and routes in Configuration, then admit a truck at Parking intake or from the gate desk.'}</Typography>
          </Box>
          )}
        </Box>
      )}
      <Dialog open={!!dialog} onClose={() => !pending && setDialog(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>Close trip</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              {dialog === 'close' && (
              <>
                <TextField {...field('reason')} required select label="Closure reason">{reasons.map((r) => <MenuItem key={r} value={r}>{label(r)}</MenuItem>)}</TextField>
                {form.reason === 'DOCUMENTS' && <TextField {...field('documentReference')} required label="Supporting document reference" />}
                {form.reason === 'DEVICE_RETURNED' && (
                  <>
                    <TextField {...field('comment')} multiline minRows={2} label="Remarks (optional)" helperText="Anything worth keeping about the unit or the load." inputProps={{ maxLength: 500 }} />
                    <Alert severity="info">The portable unit is recorded as physically returned at the same time, so it can be issued to the next truck.</Alert>
                  </>
                )}
                {['WRONG_TRIP', 'VEHICLE_EXITED_PLANT', 'OTHER'].includes(form.reason) && (
                  <TextField {...field('comment')} required multiline minRows={2} label="Remarks" helperText="What happened, in your own words. It is kept on the trip." inputProps={{ maxLength: 500 }} />
                )}
                {form.reason === 'MANUAL' && (
                <>
                  <TextField {...field('comment')} required multiline minRows={2} label="Why is this being completed manually?" inputProps={{ maxLength: 500 }} />
                  <Alert severity="warning">This completes the trip regardless of remaining checkpoints. The reason and your name are recorded against the trip.</Alert>
                </>
                )}
                <Alert severity="info">Portable devices checked out for this trip are released. Record their return on this trip once they are back.</Alert>
              </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="submit" disabled={pending} variant="contained">{pending ? 'Saving…' : 'Confirm'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={zoom && !!challan} onClose={() => setZoom(false)} fullWidth maxWidth="md">
        <DialogTitle>Challan as presented</DialogTitle>
        <DialogContent>
          <Box component="img" src={challan} alt="Challan photographed at intake" sx={{ width: '100%', display: 'block' }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setZoom(false)}>Close</Button></DialogActions>
      </Dialog>
    </PageLayout>
  );
};
export default TripsPage;
